/**
 * ContentTab — renders fields for a specific formTab key, grouped by formSection.
 *
 * @module components/form/ContentTab
 * @see FMPV-MVWC-E003-S001 I-006
 */
import React from 'react';
import type { MasterPatternViewDefinition, MasterPatternViewTabConfig } from '../../types/definition.types';
import type { MasterPatternViewCompany } from '../../types/service.types';
import { TabSection } from './TabSection';
import { FieldRenderer } from './fields/FieldRenderer';
import { OverridableFieldWrapper } from './fields/OverridableFieldWrapper';
import { computeColSpanClass, inferInputType, getAllFields } from '../../engine/fieldEngine';

export interface ContentTabProps {
  definition: MasterPatternViewDefinition;
  tabConfig: MasterPatternViewTabConfig;
  values: Record<string, unknown>;
  errors: Record<string, string>;
  onChange: (field: string, value: unknown) => void;
  readOnly?: boolean;
  fieldContext?: 'global' | 'company';
  isShowGlobalValuesEnabled?: boolean;
  globalValues?: Record<string, unknown>;
  onFieldRevert?: (fieldName: string, globalValue: unknown) => void;
  /** Current form mode — forwarded to renderFormField as context */
  formMode?: 'create' | 'edit' | 'view';
  /** This form's own company list — forwarded to a lookup field's inline "create new" so it can seed the child master's company selector instead of always defaulting to Global. */
  companies?: MasterPatternViewCompany[];
  /** This form's own currently active company (undefined/'global' when in global context) — same forwarding as `companies`. */
  activeCompanyId?: string | null;
}

export function ContentTab({
  definition,
  tabConfig,
  values,
  errors,
  onChange,
  readOnly = false,
  fieldContext,
  isShowGlobalValuesEnabled = false,
  globalValues = {},
  onFieldRevert,
  formMode,
  companies,
  activeCompanyId,
}: ContentTabProps) {
  const tabFields = getAllFields(definition)
    .filter((f) => f.showInForm === true && f.formTab === tabConfig.key)
    .sort((a, b) => (a.formOrder ?? Infinity) - (b.formOrder ?? Infinity));

  // Group by formSection
  const sections = tabConfig.sections ?? [];
  const sectionMap = new Map(sections.map((s) => [s.key, s]));
  const fieldsBySection = new Map<string, typeof tabFields>();

  for (const field of tabFields) {
    const sectionKey = field.formSection ?? '_default';
    const existing = fieldsBySection.get(sectionKey) ?? [];
    fieldsBySection.set(sectionKey, [...existing, field]);
  }

  const sectionKeys = sections.length > 0
    ? [...sections.map((s) => s.key), ...Array.from(fieldsBySection.keys()).filter((k) => !sections.find((s) => s.key === k))]
    : Array.from(fieldsBySection.keys());

  const isGlobal = definition.type === 'GLOBAL';
  const showOverrides = isGlobal && !!fieldContext;

  return (
    <div data-testid={`content-tab-${tabConfig.key}`}>
      {sectionKeys.map((sectionKey) => {
        const sectionFields = fieldsBySection.get(sectionKey) ?? [];
        if (sectionFields.length === 0) return null;
        const sectionConfig = sectionMap.get(sectionKey);
        return (
          <TabSection key={sectionKey} sectionKey={sectionKey} label={sectionConfig?.label}>
            {sectionFields.map((field) => {
              const isDisabled = typeof field.disabled === 'function'
                ? field.disabled(values)
                : (field.disabled ?? false);
              const renderer = (
                <FieldRenderer
                  key={field.fieldName}
                  field={field}
                  value={values[field.fieldName]}
                  onChange={(v) => onChange(field.fieldName, v)}
                  error={errors[field.fieldName]}
                  disabled={isDisabled}
                  readOnly={readOnly}
                  allValues={values}
                  formMode={formMode}
                  setFormValue={onChange}
                  companies={companies}
                  activeCompanyId={activeCompanyId}
                />
              );

              if (!showOverrides) return renderer;

              return (
                <OverridableFieldWrapper
                  key={field.fieldName}
                  fieldName={field.fieldName}
                  isOverridable={field.isOverridable ?? false}
                  isCompanyOnly={field.isCompanyOnly ?? false}
                  context={fieldContext!}
                  globalValue={globalValues[field.fieldName]}
                  isShowGlobalValuesEnabled={isShowGlobalValuesEnabled}
                  onRevert={onFieldRevert ?? (() => {})}
                  fieldType={field.inputType ?? inferInputType(field)}
                  className={computeColSpanClass(field)}
                >
                  {renderer}
                </OverridableFieldWrapper>
              );
            })}
          </TabSection>
        );
      })}
    </div>
  );
}
